import { ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { getRiskFactorScore, type RiskFactor } from '@revoke.cash/core/risk';
import Href from 'components/common/Href';
import RichText from 'components/common/RichText';
import { useTranslations } from 'next-intl';
import { twMerge } from 'tailwind-merge';

interface Props {
  riskFactor: RiskFactor;
}

interface DataSourceLink {
  name: string;
  url?: string;
}

const SOURCES: Record<string, DataSourceLink> = {
  scamsniffer: {
    name: 'ScamSniffer',
    url: 'https://scamsniffer.io/',
  },
  webacy: {
    name: 'Webacy',
    url: 'https://webacy.com/',
  },
  nefture: {
    name: 'Nefture',
    url: 'https://nefture.com/',
  },
  revoke: {
    name: 'Revoke.cash',
  },
};

const RiskFactorDisplay = ({ riskFactor }: Props) => {
  const t = useTranslations();

  const messageKey =
    riskFactor.type === 'exploit' && !riskFactor.data
      ? 'address.risk_factors.exploit_unnamed'
      : `address.risk_factors.${riskFactor.type}`;

  const riskFactorText = t(messageKey, {
    type: riskFactor.type,
    data: riskFactor.data ?? '',
  });

  if (riskFactorText === messageKey) return null;

  const source = SOURCES[riskFactor.source];

  const sourceDisplay = (
    <RichText>
      {(tags) =>
        t.rich('address.risk_factors.source', {
          ...tags,
          source: source?.name ?? riskFactor.source,
          'source-link': (children) =>
            source?.url ? (
              <Href href={source.url} external className="font-medium">
                {children}
              </Href>
            ) : (
              <span className="font-medium">{children}</span>
            ),
        })
      }
    </RichText>
  );

  return (
    <div className={twMerge('flex items-center justify-center gap-1 flex-wrap')}>
      <div>{getRiskIcon(riskFactor)}</div>
      <div>{riskFactorText}</div>
      {source && <div>({sourceDisplay})</div>}
    </div>
  );
};

export default RiskFactorDisplay;

const getRiskIcon = (riskFactor: RiskFactor) => {
  const score = getRiskFactorScore(riskFactor);

  if (score > 75) {
    return <ExclamationCircleIcon className=" text-red-500 h-5" />;
  }

  if (score > 0) {
    return <ExclamationTriangleIcon className="text-yellow-500 h-5" />;
  }

  // Green is only used for negative risk factors (e.g. allowlist)
  return <InformationCircleIcon className="text-green-500 h-5" />;
};
